import { DragDropModule, DragConstrainPosition } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { filter, auditTime } from 'rxjs';
import { CustomerConfigService } from '../../../core/customer/customer-config.service';
import { EventHealthSaveNotifications } from '../../../core/services/CTEs/event-health-save.interceptor';
import { normalizeHealthUid } from './event-health-targets';
import { EventHealthService } from '../../../core/services/CTEs/event-health.service';
import { healthAssessmentNotice, healthFindingExplanation, mapHealthReview } from './event-health-form-review.mapper';
import { EventHealthFormBridgeService, EventHealthFormReviewService } from './event-health-form-review.service';
import { isResolutionIncluded, normalizeEventHealthReviewLevel } from './event-health-review-level';
import {
  HealthResolutionGroup,
  HealthReviewFinding,
  HealthReviewSuggestion,
} from './event-health-form-review.types';

type VisibleResolutionGroup = Exclude<HealthResolutionGroup, 'automatic'>;

@Component({
  selector: 'app-event-health-form-review',
  standalone: true,
  imports: [DragDropModule, CommonModule, MatIconModule, MatSnackBarModule],
  templateUrl: './event-health-form-review.component.html',
  styleUrls: ['./event-health-form-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventHealthFormReviewComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bridge = inject(EventHealthFormBridgeService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly eventHealthService = inject(EventHealthService);
  private readonly customerConfig = inject(CustomerConfigService);
  private readonly saves = inject(EventHealthSaveNotifications);
  private loadSequence = 0;

  readonly session = inject(EventHealthFormReviewService);
  readonly collapsed = signal(false);
  readonly constrainDrag: DragConstrainPosition = (point, _drag, dimensions, pickup) => ({
    x: Math.max(0, Math.min(point.x - pickup.x, window.innerWidth - dimensions.width)),
    y: Math.max(0, Math.min(point.y - pickup.y, window.innerHeight - dimensions.height)),
  });
  readonly findings = this.session.findings;
  readonly resolutionGroups: ReadonlyArray<{ id: VisibleResolutionGroup; label: string; icon: string }> = [
    { id: 'form', label: 'Fix here', icon: 'edit' },
    { id: 'master-data', label: 'Master data', icon: 'link' },
    { id: 'support', label: 'Support', icon: 'support_agent' },
  ];
  readonly selectedResolutionGroup = signal<VisibleResolutionGroup>('form');
  readonly resolutionGroupCounts = computed(() => {
    const counts: Record<HealthResolutionGroup, number> = { form: 0, 'master-data': 0, automatic: 0, support: 0 };
    this.findings().forEach((finding) => counts[finding.resolution.group]++);
    return counts;
  });
  readonly showResolutionFilter = computed(() =>
    this.resolutionGroups.filter((group) => this.resolutionGroupCounts()[group.id] > 0).length > 1,
  );
  readonly automaticFindings = computed(() =>
    this.findings().filter((finding) => finding.resolution.group === 'automatic'),
  );
  readonly activeResolutionGroup = computed<VisibleResolutionGroup>(() => {
    const selected = this.selectedResolutionGroup();
    const counts = this.resolutionGroupCounts();
    if (!this.findings().length || counts[selected]) return selected;
    return this.resolutionGroups.find((group) => counts[group.id] > 0)?.id ?? 'form';
  });
  readonly visibleFindings = computed(() =>
    this.findings().filter((finding) => finding.resolution.group === this.activeResolutionGroup()),
  );
  readonly resolutionSummary = computed(() => {
    const group = this.activeResolutionGroup();
    const count = this.resolutionGroupCounts()[group];
    const noun = count === 1 ? 'finding' : 'findings';

    if (group === 'form') {
      return {
        tone: 'form', icon: 'edit',
        title: `${count} ${noun} can be corrected in this form`,
        detail: 'Select a finding to go to the relevant field.',
      };
    }
    if (group === 'master-data') {
      return {
        tone: 'master-data', icon: 'link',
        title: `${count} ${noun} ${count === 1 ? 'belongs' : 'belong'} to linked Master Data`,
        detail: 'They cannot be corrected in this event form.',
      };
    }
    return {
      tone: 'support', icon: 'support_agent',
      title: `${count} ${noun} ${count === 1 ? 'requires' : 'require'} support`,
      detail: 'This workflow information cannot be changed here.',
    };
  });
  readonly context = this.session.context;
  readonly reviewLevelLabel = computed(() => {
    const level = this.context()?.reviewLevel ?? 'full';
    if (level === 'form') return 'Basic';
    if (level === 'master-data') return 'Standard';
    return 'Full review';
  });
  readonly assessment = this.session.assessment;
  readonly errorExplanation = healthFindingExplanation;
  readonly assessmentNotice = computed(() => healthAssessmentNotice(this.context()));
  readonly actionableFindings = computed(() => this.findings().filter((finding) => this.findingKind(finding) === 'form'));
  readonly actionableCompletedCount = computed(() =>
    this.actionableFindings().filter((finding) => this.session.isLocallyChanged(finding.id) || this.session.isResolved(finding.id)).length,
  );
  readonly masterDataSummary = computed(() => {
    const context = this.context();
    const masterData = context?.masterData;
    const linkedFindings = this.findings().filter((finding) => this.findingKind(finding) === 'linked');

    if (masterData?.status === 'unavailable') {
      return { tone: 'unavailable', label: 'Linked data could not be checked', detail: 'No linked-data error has been confirmed.' };
    }
    if (linkedFindings.length) {
      return { tone: 'issue', label: 'Linked data checked — issue found', detail: 'The details are listed below. They cannot be corrected in this form.' };
    }
    if (masterData?.status === 'available' && masterData.referencedIds?.length) {
      return { tone: 'ok', label: 'Linked data checked', detail: 'No linked-data issue was found.' };
    }
    if (masterData?.status === 'not-required') {
      return { tone: 'ok', label: 'Linked data not required', detail: 'This assessment did not need linked master data.' };
    }
    if (masterData?.status === 'available') {
      return { tone: 'ok', label: 'Linked-data check available', detail: 'No linked records were referenced by this assessment.' };
    }
    return null;
  });
  readonly progress = computed(() => {
    const total = this.actionableFindings().length;
    return total ? Math.round((this.actionableCompletedCount() / total) * 100)
      : this.context()?.status === 'ready' && !this.assessmentNotice() ? 100 : 0;
  });
  readonly panelKicker = computed(() => {
    if (this.actionableFindings().length || this.automaticFindings().length) return 'Fix event health';
    if (this.findings().length) return 'Review event health';
    return 'Event health';
  });
  readonly progressLabel = computed(() => {
    if (this.actionableFindings().length) return `${this.actionableCompletedCount()} of ${this.actionableFindings().length} form fixes updated`;
    if (this.automaticFindings().length) return 'Save required · handled automatically';
    if (this.resolutionGroupCounts()['master-data']) return 'Review only · linked master data';
    if (this.resolutionGroupCounts().support) return 'Review only · support required';
    if ((this.context()?.hiddenFindingCount ?? 0) > 0) return 'Ready for selected level';
    return this.context()?.status === 'ready' ? 'Assessment complete' : 'Review only · no editable fields';
  });
  readonly emptyState = computed(() => {
    const context = this.context();
    const hidden = context?.hiddenFindingCount ?? 0;
    if (hidden > 0) {
      return {
        title: 'No findings at this review level',
        detail: `${hidden} ${hidden === 1 ? 'finding is' : 'findings are'} outside the selected level. Choose a broader review level on the Health page to include ${hidden === 1 ? 'it' : 'them'}.`,
      };
    }
    if (context?.status === 'ready') {
      return { title: 'No health findings', detail: 'This event passed the GDST 1.2 assessment.' };
    }
    return { title: 'No findings returned', detail: 'No individual findings were returned for this assessment.' };
  });
  readonly findingsHeading = computed(() => {
    if (!this.findings().length) return 'Selected review level';
    if (this.activeResolutionGroup() === 'form') return 'Fix in this form';
    if (this.activeResolutionGroup() === 'master-data') return 'Linked master data';
    return 'Needs support';
  });
  readonly primaryAction = computed<'submit' | 'return'>(() =>
    this.actionableFindings().length || this.automaticFindings().length ? 'submit' : 'return',
  );

  ngOnInit(): void {
    this.syncFromUrl(true);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncFromUrl(true));
    this.saves.saved.pipe(
      filter(uid => normalizeHealthUid(uid) === normalizeHealthUid(this.context()?.eventUid)),
      // Let existing save handlers update their forms before replacing the assessment.
      auditTime(0), takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.syncFromUrl());
  }

  toggleCollapsed(): void {
    this.collapsed.update((value) => !value);
  }

  selectResolutionGroup(group: VisibleResolutionGroup): void {
    if (this.resolutionGroupCounts()[group]) this.selectedResolutionGroup.set(group);
  }

  selectFinding(finding: HealthReviewFinding): void {
    this.collapsed.set(false);
    if (this.findingKind(finding) !== 'form') return;
    this.bridge.focusFinding(finding);
  }

  findingKind(finding: HealthReviewFinding): 'form' | 'linked' | 'generated' | 'managed' | 'support' {
    switch (finding.resolution.mechanism) {
      case 'edit-form': return 'form';
      case 'resolve-master-data': return 'linked';
      case 'generated-on-save': return 'generated';
      case 'managed-automatically': return 'managed';
      default: return 'support';
    }
  }

  findingKindLabel(finding: HealthReviewFinding): string {
    switch (this.findingKind(finding)) {
      case 'form': return 'Fix in this form';
      case 'linked': return 'Linked data issue';
      case 'generated': return 'Generated on save';
      case 'managed': return 'Managed automatically';
      default: return 'Needs support';
    }
  }

  applySuggestion(finding: HealthReviewFinding, suggestion: HealthReviewSuggestion, event: MouseEvent): void {
    event.stopPropagation();

    if (this.bridge.applySuggestion(finding, suggestion)) {
      this.snackBar.open(`${finding.target.label} updated.`, '', { duration: 2600 });
      return;
    }

    this.bridge.focusFinding(finding);
    this.snackBar.open(`Open ${finding.target.label} and choose “${suggestion.label}”.`, '', { duration: 3200 });
  }

  restoreOriginalValue(finding: HealthReviewFinding, event: MouseEvent): void {
    event.stopPropagation();

    if (this.bridge.restoreOriginalValue(finding)) {
      this.snackBar.open(`${finding.target.label} restored to its original value.`, '', { duration: 2600 });
      return;
    }

    this.snackBar.open(`The original value for ${finding.target.label} could not be restored.`, '', { duration: 3200 });
  }

  nextFinding(): void {
    const findings = this.actionableFindings();
    if (!findings.length) return;

    const index = findings.findIndex((finding) => finding.id === this.session.activeFindingId());
    this.selectFinding(findings[(index + 1) % findings.length]);
  }

  previousFinding(): void {
    const findings = this.actionableFindings();
    if (!findings.length) return;

    const index = findings.findIndex((finding) => finding.id === this.session.activeFindingId());
    this.selectFinding(findings[(index <= 0 ? findings.length : index) - 1]);
  }

  goToSubmit(): void {
    if (this.bridge.scrollToSubmit()) this.collapsed.set(true);
  }

  runPrimaryAction(): void {
    if (this.primaryAction() === 'submit') {
      this.goToSubmit();
      return;
    }
    const returnTo = this.context()?.returnTo;
    if (returnTo) void this.router.navigateByUrl(returnTo);
  }

  scopeLabel(scope: HealthReviewFinding['scope']): string {
    if (scope === 'master-data') return 'Master data';
    if (scope === 'event-or-master-data') return 'Event or master data';
    if (scope === 'traceability') return 'Traceability';
    return 'Event data';
  }

  private syncFromUrl(openDialog = false): void {
    const tree = this.router.parseUrl(this.router.url);
    const query = tree.queryParams;

    if (query['mode'] !== 'health-review' || !query['uid']) {
      this.loadSequence += 1;
      this.bridge.disconnect();
      this.session.stop();
      return;
    }

    if (openDialog) this.collapsed.set(false);

    const requestId = ++this.loadSequence;
    const eventUid = String(query['uid']);
    const requestedReturnTo = String(query['returnTo'] || '');
    const returnTo = requestedReturnTo || this.defaultHealthRoute();
    const reviewLevel = normalizeEventHealthReviewLevel(query['reviewLevel']);

    this.bridge.disconnect();
    this.session.stop();
    this.eventHealthService
      .getEventHealthByUrn(eventUid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (requestId !== this.loadSequence) return;
          const review = mapHealthReview(response, returnTo, this.customerConfig.overrides.eventHealth);
          review.context.reviewLevel = reviewLevel;
          const totalFindingCount = review.assessment.findings.length;
          review.assessment.findings = review.assessment.findings.filter((finding) =>
            isResolutionIncluded(reviewLevel, finding.resolution.group),
          );
          review.context.hiddenFindingCount = totalFindingCount - review.assessment.findings.length;
          if (
            reviewLevel !== 'full' &&
            review.context.reason !== 'schema-invalid' &&
            review.context.reason !== 'classification-incomplete' &&
            review.context.status !== 'not-assessed' &&
            review.context.status !== 'partially-assessed'
          ) {
            review.context.status = review.assessment.findings.some(
              (finding) => finding.severity === 'error' && finding.resolution.group !== 'automatic',
            )
              ? 'needs-action'
              : 'ready';
          }
          this.session.start(review.context, review.assessment);
          this.bridge.connect(review.assessment);
        },
        error: () => {
          if (requestId !== this.loadSequence) return;
          this.bridge.disconnect();
          this.session.stop();
          this.snackBar.open('The latest event health could not be loaded. Reopen health review to retry.', 'Close', { duration: 5000 });
        },
      });
  }

  private defaultHealthRoute(): string {
    return this.router.url.startsWith('/wild-harvest/')
      ? '/wild-harvest/tools/traceability/event-health'
      : '/aquaculture/traceability/event-health';
  }
}
